// src/features/registroPage/registroPage.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    MenuItem,
    InputAdornment,
    IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Email, Lock, Person, LocalHospital, MedicalServices } from '@mui/icons-material';
import authService from '../../service/authService.js';
import medico1 from '../../assets/1.jpg';
import medico2 from '../../assets/2.jpg';
import medico3 from '../../assets/3.jpg';

const images = [
    {
        url: medico1, 
        title: 'Atención médica de calidad',
        subtitle: 'Profesionales comprometidos con tu salud'
    },
    {
        url: medico2,
        title: 'Consultas rápidas y eficientes',
        subtitle: 'Gestioná tus turnos en minutos'
    },
    {
        url: medico3,
        title: 'Tecnología al servicio de la salud',
        subtitle: 'Plataforma moderna y segura'
    }
];

const RegistroPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [nombre, setNombre] = useState('');
    const [rol, setRol] = useState('PACIENTE');
    const [mostrarPassword, setMostrarPassword] = useState(false);
    const [obraSocial, setObraSocial] = useState('');
    const [plan, setPlan] = useState('');
    const [especialidad, setEspecialidad] = useState('');
    const [matricula, setMatricula] = useState('');
    const [error, setError] = useState('');
    const [cargando, setCargando] = useState(false);
    const [slideActual, setSlideActual] = useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setSlideActual((prev) => (prev + 1) % images.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const irAlSlide = (index) => {
        setSlideActual(index);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setCargando(true);

        try {
            const datos = { email, password, nombre, rol };
            if (rol === 'PACIENTE') {
                datos.obraSocial = obraSocial;
                datos.plan = plan || 'Básico';
            } else if (rol === 'MEDICO') {
                datos.especialidad = especialidad;
                datos.matricula = matricula;
            }

            await authService.registrar(datos);
            navigate('/login', {
                state: { mensaje: 'Cuenta creada exitosamente. ¡Iniciá sesión!' }
            });
        } catch (error) {
            setError(error.message || 'Error al registrar usuario');
        } finally {
            setCargando(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `
                    radial-gradient(circle at 20% 30%, rgba(213, 0, 50, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 80% 70%, rgba(213, 0, 50, 0.05) 0%, transparent 40%),
                    linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)
                `,
                p: 2
            }}
        >
            <Card
                sx={{
                    maxWidth: 1100,
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    
                    {/* FORMULARIO */}
                    <Box
                        sx={{
                            flex: 1,
                            p: 4,
                            backgroundColor: 'white',
                            minHeight: { xs: 'auto', md: '550px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 380,
                                maxHeight: { xs: 'none', md: '70vh' },
                                overflowY: { xs: 'visible', md: 'auto' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <LocalHospital sx={{ color: '#D50032', fontSize: 28 }} />
                                <Typography variant="h6" sx={{ color: '#D50032', fontWeight: 700 }}>
                                    Sweet Medical
                                </Typography>
                            </Box>

                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#363636', mb: 1 }}>
                                Registrate
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                                Completá los datos para crear tu cuenta
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    sx={{ mb: 2 }}
                                    disabled={cargando}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    label="Contraseña"
                                    type={mostrarPassword ? 'text' : 'password'}
                                    fullWidth
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    helperText="Mínimo 6 caracteres"
                                    sx={{ mb: 2 }}
                                    disabled={cargando}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock color="primary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setMostrarPassword(!mostrarPassword)}
                                                    edge="end"
                                                >
                                                    {mostrarPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    label="Nombre completo"
                                    fullWidth
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    required
                                    sx={{ mb: 2 }}
                                    disabled={cargando}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    select
                                    label="Tipo de usuario"
                                    fullWidth
                                    value={rol}
                                    onChange={(e) => setRol(e.target.value)}
                                    sx={{ mb: 2 }}
                                    disabled={cargando}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MedicalServices color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    <MenuItem value="PACIENTE">Paciente</MenuItem>
                                    <MenuItem value="MEDICO">Médico</MenuItem>
                                </TextField>

                                {rol === 'PACIENTE' && (
                                    <>
                                        <TextField
                                            label="Obra Social"
                                            fullWidth
                                            value={obraSocial}
                                            onChange={(e) => setObraSocial(e.target.value)}
                                            required
                                            sx={{ mb: 2 }}
                                            disabled={cargando}
                                            placeholder="Ej: Swiss Medical, OSDE, etc."
                                        />
                                        <TextField
                                            label="Plan (opcional)"
                                            fullWidth
                                            value={plan}
                                            onChange={(e) => setPlan(e.target.value)}
                                            sx={{ mb: 2 }}
                                            disabled={cargando}
                                            helperText="Si no tenés un plan específico, dejalo vacío"
                                            placeholder="Ej: Premium, Básico, etc."
                                        />
                                    </>
                                )}

                                {rol === 'MEDICO' && (
                                    <>
                                        <TextField
                                            label="Matrícula profesional"
                                            fullWidth
                                            value={matricula}
                                            onChange={(e) => setMatricula(e.target.value)}
                                            required
                                            sx={{ mb: 2 }}
                                            disabled={cargando}
                                            helperText="Número de matrícula del médico"
                                        />
                                    </>
                                )}

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    size="large"
                                    disabled={cargando}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        mb: 2
                                    }}
                                >
                                    {cargando ? <CircularProgress size={24} color="inherit" /> : 'Registrarse'}
                                </Button>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        ¿Ya tenés cuenta?{' '}
                                        <Link to="/login" style={{ color: '#D50032', textDecoration: 'none', fontWeight: 600 }}>
                                            Iniciá sesión
                                        </Link>
                                    </Typography>
                                </Box>
                            </form>
                        </Box>
                    </Box>

                    {/* CARRUSEL */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: { xs: '300px', md: '550px' },
                            position: 'relative',
                            overflow: 'hidden',
                            background: '#D50032',
                            display: { xs: 'none', md: 'block' }
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                backgroundImage: `url(${images[slideActual].url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                transition: 'all 0.8s ease-in-out',
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(213,0,50,0.4) 0%, rgba(0,0,0,0.3) 100%)'
                                }
                            }}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 50,
                                left: 0,
                                right: 0,
                                p: 4,
                                textAlign: 'center',
                                color: 'white',
                                zIndex: 2
                            }}
                        >
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {images[slideActual].title}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                {images[slideActual].subtitle}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 3 }}>
                                {images.map((_, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => irAlSlide(index)}
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: index === slideActual ? 'white' : 'rgba(255,255,255,0.4)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'scale(1.3)',
                                                backgroundColor: index === slideActual ? 'white' : 'rgba(255,255,255,0.7)'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: 15,
                                transform: 'translateY(-50%)',
                                zIndex: 3,
                                cursor: 'pointer',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: 36,
                                fontWeight: 'bold',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: 'white',
                                    transform: 'translateY(-50%) scale(1.2)'
                                }
                            }}
                            onClick={() => setSlideActual((prev) => (prev - 1 + images.length) % images.length)}
                        >
                            ‹
                        </Box>

                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                right: 15,
                                transform: 'translateY(-50%)',
                                zIndex: 3,
                                cursor: 'pointer',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: 36,
                                fontWeight: 'bold',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: 'white',
                                    transform: 'translateY(-50%) scale(1.2)'
                                }
                            }}
                            onClick={() => setSlideActual((prev) => (prev + 1) % images.length)}
                        >
                            ›
                        </Box>
                    </Box>

                </Box>
            </Card>
        </Box>
    );
};

export default RegistroPage;